import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { DialogWithScrollReset as Dialog } from "@/components/ui/dialog-with-scroll-reset";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDate } from "@/lib/utils";
import { User, Users, CalendarClock, Briefcase, Key, Mail, Phone, MapPin } from "lucide-react";

type EmployeeDetailsDialogProps = {
  employeeId: number | null;
  isOpen: boolean;
  onClose: () => void;
  onAssignLicense?: (employeeId: number, employeeName: string) => void;

};

export default function EmployeeDetailsDialog({
  employeeId,
  isOpen,
  onClose,
  onAssignLicense,
}: EmployeeDetailsDialogProps) {
  const [activeTab, setActiveTab] = useState<string>("overview");

  // Fetch employee details
  const { data: employee, isLoading: isLoadingEmployee } = useQuery({
    queryKey: ['/api/employees', employeeId],
    enabled: isOpen && employeeId !== null,
    queryFn: async () => {
      if (!employeeId) return null;
      try {
        console.log(`Fetching details for employee ${employeeId}`);
        const response = await apiRequest(`/api/employees/${employeeId}`);
        console.log(`Employee ${employeeId} details response:`, response);
        return response;
      } catch (error) {
        console.error(`Error fetching details for employee ${employeeId}:`, error);
        throw error;
      }
    },
  });

  // Fetch employee teams
  const { data: teams = [], isLoading: isLoadingTeams } = useQuery({
    queryKey: ['/api/employees', employeeId, 'teams'],
    enabled: isOpen && employeeId !== null,
    queryFn: async () => {
      if (!employeeId) return [];
      try {
        console.log(`Fetching teams for employee ${employeeId}`);
        const response = await apiRequest(`/api/employees/${employeeId}/teams`);
        console.log(`Employee ${employeeId} teams response:`, response);
        return response;
      } catch (error) {
        console.error(`Error fetching teams for employee ${employeeId}:`, error);
        throw error;
      }
    },
  });

  // Fetch employee licenses
  const { data: licenses = [], isLoading: isLoadingLicenses } = useQuery({
    queryKey: ['/api/employees', employeeId, 'licenses'],
    enabled: isOpen && employeeId !== null,
    queryFn: async () => {
      if (!employeeId) return [];
      try {
        console.log(`Fetching licenses for employee ${employeeId}`);
        const response = await apiRequest(`/api/employees/${employeeId}/licenses`);
        console.log(`Employee ${employeeId} licenses response:`, response);
        return response;
      } catch (error) {
        console.error(`Error fetching licenses for employee ${employeeId}:`, error);
        throw error;
      }
    },
  });

  const isLoading = isLoadingEmployee || isLoadingTeams || isLoadingLicenses;

  const handleAssignLicense = () => {
    if (onAssignLicense && employeeId && employee) {
      onClose();
      onAssignLicense(employeeId, employee.fullName);
    }
  };



  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] w-[calc(100%-2rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="break-words">
            {isLoading ? (
              <Skeleton className="h-8 w-64" />
            ) : (
              employee?.fullName || "Employee Details"
            )}
          </DialogTitle>
          <DialogDescription>
            View employee details, teams, and assigned licenses
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ) : employee ? (
          <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-3 mb-4 w-full">
              <TabsTrigger value="overview" className="text-xs sm:text-sm">
                <User className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Overview</span>
                <span className="sm:hidden">Info</span>
              </TabsTrigger>
              <TabsTrigger value="teams" className="text-xs sm:text-sm">
                <Users className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Teams</span>
                <span className="sm:hidden">Teams</span>
                <span className="ml-1">({teams.length})</span>
              </TabsTrigger>
              <TabsTrigger value="licenses" className="text-xs sm:text-sm">
                <Key className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Licenses</span>
                <span className="sm:hidden">Lic.</span>
                <span className="ml-1">({licenses.length})</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="min-h-[300px]">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Employee Information</CardTitle>
                  <CardDescription>Basic employee details and status</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <div className="text-sm text-slate-500">Full Name</div>
                      <div className="font-medium">{employee.fullName}</div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm text-slate-500">Status</div>
                      <div>
                        <Badge 
                          variant="outline"
                          className={`${
                            employee.status?.toLowerCase() === "active" ? "bg-green-100 text-green-800" :
                            employee.status?.toLowerCase() === "inactive" ? "bg-slate-100 text-slate-800" :
                            employee.status?.toLowerCase() === "onboarding" ? "bg-amber-100 text-amber-800" :
                            employee.status?.toLowerCase() === "terminated" ? "bg-red-100 text-red-800" :
                            "bg-slate-100 text-slate-800"
                          }`}
                        >
                          {employee.status || "Unknown"}
                        </Badge>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm text-slate-500">Email</div>
                      <div className="font-medium flex items-center">
                        <Mail className="h-4 w-4 mr-1 text-slate-400" />
                        <a href={`mailto:${employee.email}`} className="text-blue-600 hover:underline">
                          {employee.email}
                        </a>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm text-slate-500">Job Title</div>
                      <div className="font-medium flex items-center">
                        <Briefcase className="h-4 w-4 mr-1 text-slate-400" />
                        {employee.jobTitle || "Not specified"}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm text-slate-500">Phone</div>
                      <div className="font-medium flex items-center">
                        <Phone className="h-4 w-4 mr-1 text-slate-400" />
                        {employee.phone || "Not provided"}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm text-slate-500">Location</div>
                      <div className="font-medium flex items-center">
                        <MapPin className="h-4 w-4 mr-1 text-slate-400" />
                        {employee.location || "Not specified"}
                      </div>
                    </div>
                    {employee.hireDate && (
                      <div className="space-y-1">
                        <div className="text-sm text-slate-500">Hire Date</div>
                        <div className="font-medium flex items-center">
                          <CalendarClock className="h-4 w-4 mr-1 text-slate-400" />
                          {formatDate(new Date(employee.hireDate))}
                        </div>
                      </div>
                    )}
                  </div>

                  {employee.notes && (
                    <div className="space-y-1 pt-2 border-t">
                      <div className="text-sm text-slate-500">Notes</div>
                      <div className="text-sm">{employee.notes}</div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="teams" className="min-h-[300px]">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Team Memberships</CardTitle>
                  <CardDescription>Teams this employee belongs to</CardDescription>
                </CardHeader>
                <CardContent>
                  {teams.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p>Not a member of any teams</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[250px] pr-4">
                      <div className="space-y-2">
                        {(teams || []).map((team: { id: number; name: string; description?: string; status?: string }) => (
                          <Card key={team.id} className="p-4 shadow-sm">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="text-md font-medium">{team.name}</h4>
                                {team.description && (
                                  <p className="text-sm text-slate-500 mt-1">{team.description}</p>
                                )}
                              </div>
                              <Badge>{team.status || "active"}</Badge>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="licenses" className="min-h-[300px]">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Assigned Licenses</CardTitle>
                  <CardDescription>Software licenses assigned to this employee</CardDescription>
                </CardHeader>
                <CardContent>
                  {licenses.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Key className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p className="mb-2">No licenses assigned</p>
                      <Button variant="outline" size="sm" onClick={handleAssignLicense}>
                        Assign License
                      </Button>
                    </div>
                  ) : (
                    <ScrollArea className="h-[250px] pr-4">
                      <div className="space-y-3">
                        {(licenses || []).map((license: any) => (
                          <Card key={license.id} className="p-4 shadow-sm border-l-4 border-l-primary">
                            <div className="flex flex-col space-y-2">
                              <div className="flex justify-between">
                                <h4 className="text-md font-medium">{license.name}</h4>
                                <Badge 
                                  variant={license.status?.toLowerCase() === "active" ? "default" : "secondary"}
                                  className={license.status?.toLowerCase() === "active" ? "bg-green-100 text-green-800 hover:bg-green-100" : ""}
                                >
                                  {license.status || "unknown"}
                                </Badge>
                              </div>
                              
                              <div className="flex items-center">
                                <div className="flex-shrink-0">
                                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                    Application
                                  </Badge>
                                </div>
                                <p className="text-sm ml-2">{license.application?.name || license.applicationName || "Unknown Application"}</p>
                              </div>
                              
                              <div className="flex flex-wrap gap-2 mt-1">
                                {license.licenseType && (
                                  <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                    {license.licenseType}
                                  </Badge>
                                )}
                                {!license.licenseType && license.type && (
                                  <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                    {license.type}
                                  </Badge>
                                )}
                                
                                <Badge variant="outline" className={`${
                                  (license.assignedSeats || 0) < (license.totalSeats || 0) 
                                    ? 'bg-primary/10 text-primary border-primary/20' 
                                    : 'bg-amber-50 text-amber-700 border-amber-200'
                                }`}>
                                  {license.assignedSeats || 0}/{license.totalSeats || 0} seats
                                </Badge>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        ) : (
          <div className="text-center py-8">
            <p>No employee data found</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {activeTab === "licenses" && (
            <Button onClick={handleAssignLicense}>
              Assign License
            </Button>
          )}

        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}